import { useTranslation } from 'react-i18next';
import {
  BadgeCheck, Loader2, Clock, AlertTriangle, ShieldX,
  FileText, Check, PackageCheck, CheckCircle2, XCircle, CircleDot,
  type LucideIcon,
} from 'lucide-react';

/**
 * StatusChip — the single, shared trust/lifecycle signal for the Safaqat24
 * services layer. Every new surface (professional roles, service engagements,
 * advertisements, request boards, off-plan gating, …) renders verification and
 * workflow state through THIS one component so the vocabulary never drifts.
 *
 * It covers both the AccountActivity verification state machine
 * (DECLARED → VERIFYING → VERIFIED → EXPIRING_SOON/EXPIRED/SUSPENDED/REJECTED)
 * and the ServiceEngagement workflow
 * (REQUESTED → QUOTED → ACCEPTED → IN_PROGRESS → DELIVERED → COMPLETED/CANCELLED).
 * Unknown values fall back to a neutral chip with a title-cased label, so a new
 * backend status can never render a broken or raw token.
 *
 * Labels are translated in-component (t with a defaultValue) so callers never
 * re-implement the wording — pass `label` only to override.
 */

type Tone = 'green' | 'blue' | 'gray' | 'amber' | 'red';

/**
 * Tones, brand-aligned 2026-09-17 (THEME.md §2.4c / §2.5).
 *
 * `amber` and `red` were Tailwind's own ramps — an orange-brown amber-700 and a
 * generic red — next to a green brand. They now use the board's yellow and red
 * so a chip belongs to the same palette as everything around it.
 *
 * A `violet` tone used to exist for SOLD_OUT. Tailwind's `violet` is remapped
 * to the brand green ramp (so stray old-theme classes land on-brand), which
 * made "Sold out" render as green-on-green-tint — visually identical to
 * "Verified". Sold is red's job in the register, so the tone is gone and
 * SOLD_OUT is red.
 *
 * `green` deliberately stays on Tailwind's green, NOT brand green: THEME.md
 * §2.5 keeps success distinct from the brand so green can still mean "good"
 * on a site whose brand colour is green.
 */
const TONE_CLS: Record<Tone, string> = {
  green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  // brand cyan: #00759F on #E2F6FD is 4.66:1. The plain `text` step
  // (#007FAF) is only 4.05:1 on that tint, so the chip uses a darker one.
  blue: 'bg-[#E2F6FD] text-[#00759F] dark:bg-[#00AEEF]/15 dark:text-[#5FCBF5]',
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  // the board's yellow as a chip: #8C6A0B on #FEF8E8 is 4.62:1
  amber: 'bg-[#FEF8E8] text-[#8C6A0B] dark:bg-[#FFC107]/15 dark:text-[#FBD35A]',
  // the board's red as a chip: #C62828 on #FDE8E7 is 6.68:1
  red: 'bg-[#FDE8E7] text-[#C62828] dark:bg-[#DF2F2B]/15 dark:text-[#FF8A80]',
};

interface Entry { tone: Tone; icon: LucideIcon; label: string; spin?: boolean }

// Keyed by the canonical UPPER_SNAKE status token.
const STATUS_MAP: Record<string, Entry> = {
  // AccountActivity verification spine
  VERIFIED: { tone: 'green', icon: BadgeCheck, label: 'Verified' },
  VERIFYING: { tone: 'blue', icon: Loader2, label: 'Verifying', spin: true },
  DECLARED: { tone: 'gray', icon: Clock, label: 'Pending' },
  EXPIRING_SOON: { tone: 'amber', icon: AlertTriangle, label: 'Expiring soon' },
  EXPIRED: { tone: 'red', icon: AlertTriangle, label: 'Expired' },
  SUSPENDED: { tone: 'red', icon: ShieldX, label: 'Suspended' },
  REJECTED: { tone: 'red', icon: ShieldX, label: 'Rejected' },
  // ServiceEngagement workflow
  REQUESTED: { tone: 'gray', icon: Clock, label: 'Requested' },
  QUOTED: { tone: 'amber', icon: FileText, label: 'Quoted' },
  ACCEPTED: { tone: 'green', icon: Check, label: 'Accepted' },
  IN_PROGRESS: { tone: 'blue', icon: Loader2, label: 'In progress', spin: true },
  DELIVERED: { tone: 'blue', icon: PackageCheck, label: 'Delivered' },
  COMPLETED: { tone: 'green', icon: CheckCircle2, label: 'Completed' },
  CANCELLED: { tone: 'red', icon: XCircle, label: 'Cancelled' },
  // Project / land-scheme lifecycle
  PLANNING: { tone: 'gray', icon: Clock, label: 'Planning' },
  UNDER_CONSTRUCTION: { tone: 'amber', icon: AlertTriangle, label: 'Under construction' },
  READY: { tone: 'green', icon: CheckCircle2, label: 'Ready' },
  SOLD_OUT: { tone: 'red', icon: BadgeCheck, label: 'Sold out' },
};

const titleCase = (s: string) =>
  s.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export interface StatusChipProps {
  status: string;
  /** Override the translated label (rarely needed). */
  label?: string;
  /** 'sm' (default) or a tighter 'xs'. */
  size?: 'sm' | 'xs';
  className?: string;
}

export function StatusChip({ status, label, size = 'sm', className = '' }: StatusChipProps) {
  const { t } = useTranslation();
  const key = (status || '').toUpperCase();
  const entry: Entry = STATUS_MAP[key] ?? { tone: 'gray', icon: CircleDot, label: titleCase(key || 'Unknown') };
  const Icon = entry.icon;
  const text = label ?? t(`status.${key}`, { defaultValue: entry.label });

  const pad = size === 'xs' ? 'px-1.5 py-0.5 text-[11px] gap-0.5' : 'px-2 py-0.5 text-xs gap-1';
  const iconSize = size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <span className={`inline-flex items-center rounded-full font-medium ${pad} ${TONE_CLS[entry.tone]} ${className}`}>
      <Icon className={`${iconSize}${entry.spin ? ' animate-spin' : ''}`} aria-hidden="true" />
      {text}
    </span>
  );
}

export default StatusChip;
